'use client'

import { Button, buttonVariants } from '@/components/Button'
import { Input } from '@/components/Forms'
import { Label } from '@/components/Forms/Label'
import { I18NText } from '@/components/I18NText'
import { SendEmail } from '@/components/Icons'
import { Paragraph } from '@/components/Typography/Paragraph'
import { useI18N } from '@/context/I18NContext'
import { cn } from '@/lib'
import { mightFail } from '@carbonara/utils'
import { useReCaptcha } from 'next-recaptcha-v3'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

interface Props {
	mode: 'login' | 'register'
	action: (
		data: EmailFormValues,
		token: string | null,
	) => Promise<boolean | string>
	recaptchaSiteKey: string
	onSuccess?: () => void
	email?: string
}

interface EmailFormValues {
	email: string
	name: string
	phone: string
	company: string
	acceptTerms: boolean
	next?: string
}

export const EmailForm: React.FC<Props> = ({
	mode,
	action,
	onSuccess,
	recaptchaSiteKey,
	email,
}) => {
	const searchParams = useSearchParams()
	const { getTranslation } = useI18N()
	const { executeRecaptcha } = useReCaptcha()

	const form = useForm<EmailFormValues>({
		defaultValues: {
			next: searchParams.get('next') || undefined,
			email: email || '',
		},
	})
	const [isPending, setIsPending] = useState(false)
	const [success, setSuccess] = useState(false)
	const [error, setError] = useState('')
	const labelClassName = 'text-lg font-normal'

	const onSubmit = async (data: EmailFormValues) => {
		setError('')
		setIsPending(true)

		const token = await executeRecaptcha(mode)
		const { result, error } = await mightFail(action(data, token))

		if (error || result !== true) {
			setError(`${result}`)

			setIsPending(false)
			return
		}

		if (result) {
			setSuccess(true)
			onSuccess?.()
			setIsPending(false)
		}
	}

	return (
		<>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				{!success ? (
					<div className="flex flex-col gap-5">
						{mode === 'register' && (
							<Input
								{...form.register('name', { required: true })}
								label={<I18NText text="users.name" />}
								name="name"
								type="text"
								disabled={isPending}
								labelClassName={labelClassName}
								error={
									form.formState.errors.name && (
										<I18NText
											text="forms.fieldIsRequired"
											values={{ field: <I18NText text="users.name" /> }}
										/>
									)
								}
							/>
						)}

						<Input
							{...form.register('email', {
								required: true,
								validate: (value) =>
									z.string().email().safeParse(value).success ||
									getTranslation('forms.fieldIsInvalid', {
										field: <I18NText text="users.email" />,
									}),
							})}
							readOnly={!!email}
							label={<I18NText text="users.email" />}
							name="email"
							type="email"
							placeholder="john.doe@email.com"
							disabled={isPending}
							labelClassName={labelClassName}
							error={
								form.formState.errors.email &&
								(form.formState.errors.email.message || (
									<I18NText
										text="forms.fieldIsRequired"
										values={{ field: <I18NText text="users.email" /> }}
									/>
								))
							}
						/>
						{mode === 'register' && (
							<>
								<Input
									{...form.register('phone', { required: true })}
									label={<I18NText text="users.phone" />}
									name="phone"
									type="text"
									disabled={isPending}
									labelClassName={labelClassName}
									error={
										form.formState.errors.phone && (
											<I18NText
												text="forms.fieldIsRequired"
												values={{ field: <I18NText text="users.phone" /> }}
											/>
										)
									}
								/>
								<Input
									{...form.register('company', { required: true })}
									label={<I18NText text="users.company" />}
									name="company"
									type="text"
									disabled={isPending}
									labelClassName={labelClassName}
									error={
										form.formState.errors.company && (
											<I18NText
												text="forms.fieldIsRequired"
												values={{ field: <I18NText text="users.company" /> }}
											/>
										)
									}
								/>
								<div>
									<div className="flex gap-2 items-start">
										<div className="w-8">
											<Input
												type="checkbox"
												disabled={isPending}
												id="accepted-terms"
												{...form.register('acceptTerms', { required: true })}
											/>
										</div>
										<Label
											className="flex-1 text-gray-400 pt-1"
											htmlFor="accepted-terms"
										>
											<I18NText text={`${mode}.acceptTerms`} />{' '}
											<Link
												target="_blank"
												rel="noreferrer"
												href="https://klarakarbon.no/privacy-policy"
												className={cn(
													buttonVariants({ variant: 'text' }),
													'px-0 text-inherit text-sm',
												)}
											>
												<I18NText text={`${mode}.privacyPolicy`} />
											</Link>
										</Label>
									</div>
									{form.formState.errors.acceptTerms && (
										<p className="text-red-500 text-sm mt-[2px]">
											<I18NText text={`${mode}.acceptTermsError`} />
										</p>
									)}
								</div>
							</>
						)}
						{error && (
							<Paragraph className="text-center text-red-700">
								{error}
							</Paragraph>
						)}
						<Button
							type="submit"
							variant="primary"
							size="2lg"
							className="w-full mt-2"
							loading={isPending}
						>
							<I18NText text={`${mode}.submit`} />
						</Button>
					</div>
				) : (
					<div className="mt-2 flex flex-col gap-6 items-center">
						<SendEmail className="size-18" />
						<div className="text-center px-4">
							<I18NText
								text={`${mode}.emailCheckDescription`}
								values={{
									email: form.getValues('email'),
									b: (text: string) => (
										<span className="font-medium" key={text}>
											{text}
										</span>
									),
								}}
							/>
						</div>
						<Button
							type="submit"
							variant="primary"
							size="2lg"
							className="w-full mt-10"
							loading={isPending}
						>
							<I18NText text={`${mode}.resend`} />
						</Button>
					</div>
				)}
			</form>
		</>
	)
}
